// src/components/PWASessionStatus.js - NEW FILE

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Smartphone, Clock } from 'lucide-react';
import { isPWA, isPWASessionValid } from '../utils/pwaDetection';

const StatusBadge = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.95), rgba(56, 142, 60, 0.95));
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 9998;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
  
  .icon {
    display: flex;
    align-items: center;
  }
  
  .text {
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    top: auto;
    bottom: 90px;
    right: 1rem;
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

const InfoPopup = styled.div`
  position: fixed;
  top: 4rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(76, 175, 80, 0.4);
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 300px;
  z-index: 9999;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.5);
  
  h4 {
    color: #4CAF50;
    margin: 0 0 1rem 0;
    font-size: 1rem;
  }
  
  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
    
    .label {
      color: rgba(255, 255, 255, 0.7);
    }
    
    .value {
      color: white;
      font-weight: 600;
    }
  }
  
  .note {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.4;
  }
  
  @media (max-width: 768px) {
    top: auto;
    bottom: 140px;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
`;

const PWASessionStatus = () => {
  const [showInfo, setShowInfo] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isPWAMode, setIsPWAMode] = useState(false);

  useEffect(() => {
    const pwaMode = isPWA();
    setIsPWAMode(pwaMode);
    
    if (pwaMode) {
      updateSessionInfo();
      
      // Update session info every minute
      const interval = setInterval(updateSessionInfo, 60000);
      return () => clearInterval(interval);
    }
  }, []);

  const updateSessionInfo = () => {
    const loginTimestamp = localStorage.getItem('pwa_login_timestamp');
    const lastActive = localStorage.getItem('pwa_last_active');
    
    if (loginTimestamp) {
      const loginDate = new Date(parseInt(loginTimestamp));
      const now = new Date();
      const daysSinceLogin = Math.floor((now - loginDate) / (1000 * 60 * 60 * 24));
      const daysRemaining = Math.max(0, 7 - daysSinceLogin);
      
      setSessionInfo({
        loginDate,
        daysSinceLogin,
        daysRemaining,
        isValid: isPWASessionValid(),
        lastActive: lastActive ? new Date(parseInt(lastActive)) : null
      });
    }
  };

  if (!isPWAMode) return null;

  return (
    <>
      {showInfo && sessionInfo && (
        <InfoPopup onClick={() => setShowInfo(false)}>
          <h4>📱 PWA Session Info</h4>
          
          <div className="info-row">
            <div className="label">Logged in:</div>
            <div className="value">
              {sessionInfo.daysSinceLogin} {sessionInfo.daysSinceLogin === 1 ? 'day' : 'days'} ago
            </div>
          </div>
          
          <div className="info-row">
            <div className="label">Session expires:</div>
            <div className="value">
              {sessionInfo.daysRemaining} {sessionInfo.daysRemaining === 1 ? 'day' : 'days'}
            </div>
          </div>
          
          <div className="info-row">
            <div className="label">Status:</div>
            <div className="value" style={{ color: sessionInfo.isValid ? '#4CAF50' : '#F44336' }}>
              {sessionInfo.isValid ? '✓ Active' : '✗ Expired'}
            </div>
          </div>
          
          <div className="note">
            💡 Your session persists for 7 days even when the app is closed. 
            Stay active to maintain continuous access.
          </div>
        </InfoPopup>
      )}
    </>
  );
};

export default PWASessionStatus;